import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import logo from './wysupp-icon-green-transparent.svg';
import AccountInfo from './AccountInfo';
import { useAuth } from './AuthContext';
import { useUploadProgress } from './UploadProgressContext';
import { FaCheckCircle, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';

// --- REMOVED PROPS --- userProjects, selectedProjectName, onProjectChange
export default function Header() {
    const { user, selectedProjectName, updateSelectedProject } = useAuth();
    const userProjects = user?.projects || [];
    const { jobs } = useUploadProgress();
    const processingJobs = jobs.filter(j => j.status === 'processing');
    const doneJobs = jobs.filter(j => j.status !== 'processing');

    // --- NEW STATE for confirmation message ---
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [confirmationTimeoutId, setConfirmationTimeoutId] = useState(null);
    // ------------------------------------------

    // --- Handler now uses updateSelectedProject from context and shows confirmation ---
    const handleProjectChange = (e) => {
        const newProjectName = e.target.value;
        updateSelectedProject(newProjectName);

        // --- Show confirmation message ---
        setShowConfirmation(true);
        // Clear previous timeout if user changes selection quickly
        if (confirmationTimeoutId) {
            clearTimeout(confirmationTimeoutId);
        }
        // Set new timeout to hide the message
        const timeoutId = setTimeout(() => {
            setShowConfirmation(false);
        }, 2500); // Hide after 2.5 seconds
        setConfirmationTimeoutId(timeoutId);
        // ---------------------------------
    };
    // -----------------------------------------------------

    return (
        <>
            <header className="dashboard-header">
                <Link to="/home" style={{ textDecoration: 'none' }}>
                    <div className="logo-section" style={{ cursor: 'pointer' }}>
                        <img src={logo} className="logo-icon" alt="Wysupp Logo" />
                        <span className="logo-text">Wysupp Validate</span>
                        <span className="logo-subtext">Data Quality Management</span>
                    </div>
                </Link>

                {/* --- UPDATED --- Use context data directly */}
                {userProjects.length > 0 && (
                    <div className="header-project-selector-wrapper"> {/* <-- Wrapper added */}
                        <div className="header-project-selector">
                            <label htmlFor="header-project-select">Project:</label>
                            <select
                                id="header-project-select"
                                value={selectedProjectName} // Use state from context
                                onChange={handleProjectChange} // Use new handler
                                disabled={userProjects.length === 0}
                            >
                                {userProjects.map(p => (
                                    <option key={p.id} value={p.project_name}>
                                        {p.project_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {/* --- NEW: Confirmation Message --- */}
                        <div className={`project-selected-confirmation ${showConfirmation ? 'visible' : ''}`}>
                            <FaCheckCircle /> Selected!
                        </div>
                        {/* ---------------------------------- */}
                    </div>
                )}
                {/* ------------------------------------------- */}

                {jobs.length > 0 && (
                    <div className="header-upload-indicator">
                        {processingJobs.length > 0 && (
                            <Link to="/interface-upload" className="upload-indicator-pill processing">
                                <FaSpinner className="upload-indicator-spin" />
                                <span>
                                    {processingJobs.length === 1
                                        ? processingJobs[0].interfaceName.replace(/_/g, ' ')
                                        : `${processingJobs.length} uploads`} uploading… {Math.round(50 + (processingJobs[0].progress || 0) / 2)}%
                                </span>
                            </Link>
                        )}
                        {doneJobs.map(job => (
                            <div key={job.id} className={`upload-indicator-pill ${job.status}`}>
                                {job.status === 'completed' ? <FaCheckCircle /> : <FaExclamationTriangle />}
                                <Link to="/interface-upload">
                                    {job.interfaceName.replace(/_/g, ' ')} {job.status === 'completed' ? 'done' : 'failed'}
                                </Link>
                            </div>
                        ))}
                    </div>
                )}

                <AccountInfo />
            </header>

            {/* Style tag includes new styles */}
            <style>{`
                /* --- Styles for the Header --- */
                /* --- Root variables (if not defined globally) --- */
                :root {
                    --dark-bg: #1a202c; --card-bg: #2d3748; --text-color: #e2e8f0; --light-text: #a0aec0;
                    --green-accent: #48bb78; --border-color: #4a5568; --input-bg: #1a202c;
                }

                .dashboard-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem 2rem;
                    background-color: var(--card-bg);
                    border-bottom: 1px solid var(--border-color);
                    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                    gap: 1.5rem;
                    position: sticky;
                    top: 0;
                    z-index: 1000;
                }

                .logo-section {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    flex-shrink: 0;
                }

                .logo-icon { width: 30px; height: 30px; }
                .logo-text { font-size: 1.25rem; font-weight: 700; color: var(--green-accent); white-space: nowrap; }
                .logo-subtext { font-size: 0.75rem; color: var(--light-text); margin-left: 0.5rem; padding-left: 0.5rem; border-left: 1px solid var(--light-text); white-space: nowrap; }

                /* --- NEW: Wrapper for selector and confirmation --- */
                .header-project-selector-wrapper {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem; /* Space between selector and message */
                    flex-grow: 1; /* Allow wrapper to take space */
                    min-width: 150px;
                    max-width: 500px; /* Adjust max-width to accommodate message */
                }
                /* --------------------------------------------------- */

                .header-project-selector {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    flex-grow: 1; /* Selector itself still grows */
                    /* Removed min/max width here, handled by wrapper */
                }

                .header-project-selector label {
                    font-size: 0.9rem;
                    font-weight: 500;
                    color: var(--light-text);
                    white-space: nowrap;
                }

                .header-project-selector select {
                    flex-grow: 1;
                    padding: 0.5rem 0.75rem;
                    background-color: var(--input-bg);
                    border: 1px solid var(--border-color);
                    border-radius: 6px;
                    color: var(--text-color);
                    font-size: 0.9rem;
                    min-width: 100px;
                    cursor: pointer;
                }
                .header-project-selector select:focus {
                    outline: none;
                    border-color: var(--green-accent);
                }

                /* --- NEW: Confirmation Message Styles --- */
                .project-selected-confirmation {
                    display: flex;
                    align-items: center;
                    gap: 0.3rem;
                    color: var(--green-accent);
                    font-size: 0.85rem;
                    font-weight: 600;
                    opacity: 0;
                    transition: opacity 0.3s ease-in-out;
                    white-space: nowrap; /* Prevent wrapping */
                }
                .project-selected-confirmation.visible {
                    opacity: 1;
                }
                /* --------------------------------------- */


                /* --- Background upload indicator --- */
                .header-upload-indicator {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    flex-shrink: 0;
                }
                .upload-indicator-pill {
                    display: flex;
                    align-items: center;
                    gap: 0.4rem;
                    padding: 0.35rem 0.75rem;
                    border-radius: 99px;
                    font-size: 0.8rem;
                    font-weight: 600;
                    white-space: nowrap;
                    text-decoration: none;
                    border: 1px solid transparent;
                    transition: opacity 0.2s;
                }
                .upload-indicator-pill.processing {
                    background-color: rgba(59, 130, 246, 0.12);
                    color: #93c5fd;
                    border-color: #3b82f6;
                }
                .upload-indicator-pill.processing:hover { opacity: 0.8; }
                .upload-indicator-pill.completed {
                    background-color: rgba(16, 185, 129, 0.12);
                    color: #6ee7b7;
                    border-color: #34d399;
                }
                .upload-indicator-pill.error {
                    background-color: rgba(239, 68, 68, 0.12);
                    color: #fca5a5;
                    border-color: #ef4444;
                }
                .upload-indicator-pill a {
                    color: inherit;
                    text-decoration: none;
                }
                .upload-indicator-pill a:hover { text-decoration: underline; }
                .upload-indicator-spin {
                    animation: spin 1s linear infinite;
                }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                .upload-indicator-dismiss {
                    background: none;
                    border: none;
                    color: inherit;
                    cursor: pointer;
                    padding: 0;
                    opacity: 0.6;
                    font-size: 0.75rem;
                    line-height: 1;
                    margin-left: 0.2rem;
                }
                .upload-indicator-dismiss:hover { opacity: 1; }
                /* ------------------------------------ */

                /* Ensure AccountInfo doesn't shrink */
                .dashboard-header > div:last-child {
                    flex-shrink: 0;
                }

                /* Media query for smaller screens */
                @media (max-width: 768px) {
                    .dashboard-header {
                        flex-direction: column;
                        align-items: stretch;
                        padding: 1rem;
                    }
                    /* --- UPDATED: Apply margin to wrapper --- */
                    .header-project-selector-wrapper {
                        max-width: none;
                        margin-top: 0.5rem;
                        margin-bottom: 0.5rem;
                        justify-content: center; /* Center items in wrapper */
                    }
                    .header-project-selector {
                       flex-grow: 0; /* Don't let selector grow excessively */
                       max-width: 300px; /* Limit selector width */
                    }
                    /* ------------------------------------- */
                    .logo-section {
                        justify-content: center;
                    }
                    .project-selected-confirmation {
                       font-size: 0.8rem; /* Slightly smaller on mobile */
                    }
                }
            `}</style>
        </>
    );
}

